import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { useMemo, useState } from 'react'
import { Button } from '../../../components/ui/button'
import { Card } from '../../../components/ui/card'
import { supabase } from '../../../lib/supabase'
import { useAuthStore } from '../../../stores/authStore'
import type { SnapshotDetailRow } from './interfaces/MonthlyRecord'
import {
  SNAPSHOT_COMPARABLE_FIELDS,
  type SnapshotComparableField,
} from './monthlyRecordsState'

interface SnapshotMeta {
  id: string
  snapshotted_at: string
  total_rooms: number
  total_grand: number
  note: string | null
}

interface SnapshotCompareModalProps {
  month: string
  onClose: () => void
}

const FIELD_LABELS: Record<SnapshotComparableField, string> = {
  price_monthly: 'Rent',
  elec_unit_price: 'Elec /Unit',
  elec_prev_reading: 'Elec Prev',
  elec_curr_reading: 'Elec Curr',
  water_unit_price: 'Water /Unit',
  water_prev_reading: 'Water Prev',
  water_curr_reading: 'Water Curr',
  internet_fee: 'Internet',
  furniture_fee: 'Furniture',
  maintenance_fee: 'Maintain',
  other_fee: 'Other',
  other_fee_label: 'Other Label',
  record_date: 'Date',
  payment_status: 'Status',
  remark: 'Remark',
  room_type: 'Room Type',
  room_status: 'Room Status',
  room_remark: 'Room Note',
  repair_details: 'Repair Details',
  keycards: 'Keycards',
}

function formatTimestamp(iso: string): string {
  return new Date(iso).toLocaleString('en-GB', {
    day: '2-digit',
    month: 'short',
    year: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
    second: '2-digit',
  })
}

function getRowFieldValue(
  row: SnapshotDetailRow,
  field: SnapshotComparableField,
): string {
  switch (field) {
    case 'room_status':
      return row.room_status ?? ''
    case 'room_remark':
      return row.room_remark ?? ''
    case 'keycards':
      return (row.keycards ?? []).join(', ')
    case 'remark':
      return row.remark ?? ''
    case 'repair_details':
      return row.repair_details ?? ''
    case 'other_fee_label':
      return row.other_fee_label ?? ''
    case 'record_date':
      return row.record_date ?? ''
    case 'payment_status':
      return row.payment_status ?? ''
    default:
      return row[field] != null ? String(row[field]) : ''
  }
}

type DiffRow = {
  roomNumber: string
  tenantNameA: string | null
  tenantNameB: string | null
  status: 'added' | 'removed' | 'changed' | 'unchanged'
  diffs: Partial<Record<SnapshotComparableField, { a: string; b: string }>>
}

function computeDiff(
  rowsA: SnapshotDetailRow[],
  rowsB: SnapshotDetailRow[],
): { diffRows: DiffRow[]; diffFields: SnapshotComparableField[] } {
  const mapA = new Map(rowsA.map((r) => [r.room_number, r]))
  const mapB = new Map(rowsB.map((r) => [r.room_number, r]))
  const allRooms = [...new Set([...mapA.keys(), ...mapB.keys()])].sort()

  const diffRows: DiffRow[] = []
  const changedFieldSet = new Set<SnapshotComparableField>()

  for (const room of allRooms) {
    const a = mapA.get(room)
    const b = mapB.get(room)

    if (!a) {
      diffRows.push({
        roomNumber: room,
        tenantNameA: null,
        tenantNameB: b?.tenant_name ?? null,
        status: 'added',
        diffs: {},
      })
      continue
    }

    if (!b) {
      diffRows.push({
        roomNumber: room,
        tenantNameA: a.tenant_name ?? null,
        tenantNameB: null,
        status: 'removed',
        diffs: {},
      })
      continue
    }

    const diffs: Partial<
      Record<SnapshotComparableField, { a: string; b: string }>
    > = {}
    for (const field of SNAPSHOT_COMPARABLE_FIELDS) {
      const va = getRowFieldValue(a, field)
      const vb = getRowFieldValue(b, field)
      if (va !== vb) {
        diffs[field] = { a: va, b: vb }
        changedFieldSet.add(field)
      }
    }

    diffRows.push({
      roomNumber: room,
      tenantNameA: a.tenant_name ?? null,
      tenantNameB: b.tenant_name ?? null,
      status: Object.keys(diffs).length > 0 ? 'changed' : 'unchanged',
      diffs,
    })
  }

  const diffFields = SNAPSHOT_COMPARABLE_FIELDS.filter((f) =>
    changedFieldSet.has(f),
  )
  return { diffRows, diffFields }
}

export function SnapshotCompareModal({
  month,
  onClose,
}: SnapshotCompareModalProps): React.ReactElement {
  const userId = useAuthStore((s) => s.user?.id)
  const queryClient = useQueryClient()

  const { data: snapshots = [], isLoading: loadingList } = useQuery<
    SnapshotMeta[]
  >({
    queryKey: ['monthly_snapshots', 'list', month],
    queryFn: async () => {
      const { data, error } = await supabase
        .from('monthly_snapshots')
        .select('id, snapshotted_at, total_rooms, total_grand, note')
        .eq('snapshot_month', month)
        .is('deleted_at', null)
        .order('snapshotted_at', { ascending: false })
      if (error) throw error
      return data as SnapshotMeta[]
    },
  })

  const [snapshotAId, setSnapshotAId] = useState<string>('')
  const [snapshotBId, setSnapshotBId] = useState<string>('')

  const resolvedAId =
    snapshotAId || (snapshots.length >= 2 ? snapshots[1].id : '')
  const resolvedBId =
    snapshotBId || (snapshots.length >= 1 ? snapshots[0].id : '')

  const isSameSnapshot = !!resolvedAId && resolvedAId === resolvedBId

  const { data: rowsA = [], isLoading: loadingA } = useQuery<
    SnapshotDetailRow[]
  >({
    queryKey: ['monthly_snapshot_rows', resolvedAId],
    enabled: !!resolvedAId && !isSameSnapshot,
    queryFn: async () => {
      const { data, error } = await supabase
        .from('monthly_snapshot_rows')
        .select('*')
        .eq('snapshot_id', resolvedAId)
        .order('room_number')
      if (error) throw error
      return data as SnapshotDetailRow[]
    },
  })

  const { data: rowsB = [], isLoading: loadingB } = useQuery<
    SnapshotDetailRow[]
  >({
    queryKey: ['monthly_snapshot_rows', resolvedBId],
    enabled: !!resolvedBId && !isSameSnapshot,
    queryFn: async () => {
      const { data, error } = await supabase
        .from('monthly_snapshot_rows')
        .select('*')
        .eq('snapshot_id', resolvedBId)
        .order('room_number')
      if (error) throw error
      return data as SnapshotDetailRow[]
    },
  })

  const isLoadingRows = loadingA || loadingB

  const { diffRows, diffFields } = useMemo(() => {
    if (isSameSnapshot || rowsA.length === 0 || rowsB.length === 0) {
      return { diffRows: [], diffFields: [] as SnapshotComparableField[] }
    }
    return computeDiff(rowsA, rowsB)
  }, [rowsA, rowsB, isSameSnapshot])

  const changedRooms = diffRows.filter((r) => r.status === 'changed')
  const addedRooms = diffRows.filter((r) => r.status === 'added')
  const removedRooms = diffRows.filter((r) => r.status === 'removed')
  const unchangedCount = diffRows.filter((r) => r.status === 'unchanged').length

  const snapshotAMeta = snapshots.find((s) => s.id === resolvedAId)
  const snapshotBMeta = snapshots.find((s) => s.id === resolvedBId)

  const handleBackdropClick = (e: React.MouseEvent<HTMLDivElement>) => {
    if (e.target === e.currentTarget) onClose()
  }

  const handleBackdropKeyDown = (e: React.KeyboardEvent<HTMLDivElement>) => {
    if (e.key === 'Escape') onClose()
  }

  const mergeMutation = useMutation({
    mutationFn: async () => {
      if (!userId || !snapshotAMeta || !snapshotBMeta) return

      const timeA = new Date(snapshotAMeta.snapshotted_at).getTime()
      const timeB = new Date(snapshotBMeta.snapshotted_at).getTime()

      const newestRows = timeA > timeB ? rowsA : rowsB
      const oldestRows = timeA > timeB ? rowsB : rowsA

      const mapNewest = new Map(newestRows.map((r) => [r.room_number, r]))
      const mapOldest = new Map(oldestRows.map((r) => [r.room_number, r]))

      const allRooms = [
        ...new Set([...mapNewest.keys(), ...mapOldest.keys()]),
      ].sort()

      let totalGrand = 0
      const mergedRowsPayload: Omit<
        SnapshotDetailRow,
        'id' | 'snapshot_id' | 'created_at'
      >[] = []

      for (const room of allRooms) {
        const row = mapNewest.get(room) || mapOldest.get(room)
        if (row) {
          totalGrand += Number(row.grand_total) || 0
          const { id, snapshot_id, created_at, ...rest } = row
          mergedRowsPayload.push(rest)
        }
      }

      const { data: snapshot, error: snapErr } = await supabase
        .from('monthly_snapshots')
        .insert({
          snapshot_month: month,
          created_by: userId,
          total_rooms: mergedRowsPayload.length,
          total_grand: totalGrand,
          note: 'Auto-merged from snapshots',
        })
        .select('id')
        .single()

      if (snapErr || !snapshot)
        throw snapErr ?? new Error('Snapshot insert failed')

      const finalRows = mergedRowsPayload.map((r) => ({
        ...r,
        snapshot_id: snapshot.id,
      }))
      const { error: rowsErr } = await supabase
        .from('monthly_snapshot_rows')
        .insert(finalRows)

      if (rowsErr) throw rowsErr
    },
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: ['monthly_snapshots', 'list', month],
      })
      queryClient.invalidateQueries({ queryKey: ['monthly_snapshot_rows'] })
      queryClient.invalidateQueries({
        queryKey: ['monthly_snapshots', 'count', month],
      })
      onClose()
    },
  })

  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 p-4"
      aria-modal="true"
      aria-label="Compare Snapshots"
      tabIndex={-1}
      onClick={handleBackdropClick}
      onKeyDown={handleBackdropKeyDown}
    >
      <Card
        className="flex w-full max-w-6xl flex-col gap-0 overflow-hidden p-0"
        style={{ maxHeight: '90vh' }}
      >
        <div className="flex items-center justify-between border-gray-200 border-b px-6 py-4">
          <div>
            <h3 className="font-semibold text-gray-900 text-lg">
              Compare Snapshots
            </h3>
            <p className="text-gray-500 text-sm">
              Compare two saved snapshots for{' '}
              <span className="font-medium text-gray-700">{month}</span>
            </p>
          </div>
          <div className="flex items-center gap-2">
            {!isSameSnapshot && (
              <Button
                size="sm"
                onClick={() => mergeMutation.mutate()}
                disabled={mergeMutation.isPending || isLoadingRows}
              >
                {mergeMutation.isPending
                  ? 'Merging...'
                  : 'Merge & Create Snapshot'}
              </Button>
            )}
            <Button variant="outline" size="sm" onClick={onClose}>
              Close
            </Button>
          </div>
        </div>

        <div className="flex flex-wrap items-center gap-4 border-gray-200 border-b bg-gray-50 px-6 py-3">
          {loadingList ? (
            <span className="text-gray-400 text-sm">Loading snapshots...</span>
          ) : snapshots.length < 2 ? (
            <span className="text-amber-600 text-sm">
              Only {snapshots.length} snapshot
              {snapshots.length !== 1 ? 's' : ''} found for {month}. Need at
              least 2 to compare.
            </span>
          ) : (
            <>
              <div className="flex items-center gap-2">
                <span className="rounded bg-slate-200 px-2 py-0.5 font-semibold text-slate-700 text-xs">
                  A Before
                </span>
                <select
                  aria-label="Snapshot A (before)"
                  value={resolvedAId}
                  onChange={(e) => setSnapshotAId(e.target.value)}
                  className="h-8 rounded border border-gray-200 bg-white px-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-400"
                >
                  {[...snapshots].reverse().map((s, i) => (
                    <option key={s.id} value={s.id}>
                      #{i + 1} {formatTimestamp(s.snapshotted_at)}
                      {s.note ? ` — ${s.note}` : ''} ({s.total_rooms} rooms)
                    </option>
                  ))}
                </select>
              </div>

              <span className="font-bold text-gray-400">to</span>

              <div className="flex items-center gap-2">
                <span className="rounded bg-blue-100 px-2 py-0.5 font-semibold text-blue-700 text-xs">
                  B After
                </span>
                <select
                  aria-label="Snapshot B (after)"
                  value={resolvedBId}
                  onChange={(e) => setSnapshotBId(e.target.value)}
                  className="h-8 rounded border border-gray-200 bg-white px-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-400"
                >
                  {snapshots.map((s, i) => (
                    <option key={s.id} value={s.id}>
                      #{snapshots.length - i}{' '}
                      {formatTimestamp(s.snapshotted_at)}
                      {s.note ? ` — ${s.note}` : ''} ({s.total_rooms} rooms)
                    </option>
                  ))}
                </select>
              </div>

              {isSameSnapshot && (
                <span className="text-amber-600 text-xs">
                  Select two different snapshots to compare
                </span>
              )}

              {!isLoadingRows && !isSameSnapshot && diffRows.length > 0 && (
                <div className="ml-auto flex flex-wrap items-center gap-2 text-xs">
                  {changedRooms.length > 0 && (
                    <span className="rounded-full bg-amber-100 px-2 py-0.5 font-semibold text-amber-700">
                      {changedRooms.length} changed
                    </span>
                  )}
                  {addedRooms.length > 0 && (
                    <span className="rounded-full bg-blue-100 px-2 py-0.5 font-semibold text-blue-700">
                      +{addedRooms.length} added
                    </span>
                  )}
                  {removedRooms.length > 0 && (
                    <span className="rounded-full bg-red-100 px-2 py-0.5 font-semibold text-red-700">
                      -{removedRooms.length} removed
                    </span>
                  )}
                  {unchangedCount > 0 && (
                    <span className="rounded-full bg-gray-100 px-2 py-0.5 font-medium text-gray-500">
                      {unchangedCount} unchanged
                    </span>
                  )}
                </div>
              )}
            </>
          )}
        </div>

        {snapshotAMeta && snapshotBMeta && !isSameSnapshot && (
          <div className="flex items-center gap-4 border-gray-200 border-b bg-white px-6 py-2 text-sm">
            <span className="text-gray-500">Grand Total:</span>
            <span className="font-semibold text-slate-700 tabular-nums">
              {snapshotAMeta.total_grand.toLocaleString()}
            </span>
            <span className="text-gray-400">to</span>
            <span
              className={
                snapshotBMeta.total_grand > snapshotAMeta.total_grand
                  ? 'font-semibold text-green-700 tabular-nums'
                  : snapshotBMeta.total_grand < snapshotAMeta.total_grand
                    ? 'font-semibold text-red-600 tabular-nums'
                    : 'font-semibold text-gray-700 tabular-nums'
              }
            >
              {snapshotBMeta.total_grand.toLocaleString()}
            </span>
            {snapshotBMeta.total_grand !== snapshotAMeta.total_grand && (
              <span
                className={
                  snapshotBMeta.total_grand > snapshotAMeta.total_grand
                    ? 'text-green-600 text-xs'
                    : 'text-red-500 text-xs'
                }
              >
                (
                {snapshotBMeta.total_grand > snapshotAMeta.total_grand
                  ? '+'
                  : ''}
                {(
                  snapshotBMeta.total_grand - snapshotAMeta.total_grand
                ).toLocaleString()}
                )
              </span>
            )}
          </div>
        )}

        <div className="min-h-0 flex-1 overflow-auto">
          {isLoadingRows && !isSameSnapshot ? (
            <div className="flex items-center justify-center py-16 text-gray-400 text-sm">
              Loading snapshot data...
            </div>
          ) : isSameSnapshot ? null : diffRows.length === 0 &&
            rowsA.length > 0 &&
            rowsB.length > 0 ? (
            <div className="flex flex-col items-center justify-center gap-2 py-16">
              <p className="font-medium text-gray-700">No differences found</p>
              <p className="text-gray-400 text-sm">
                These two snapshots are identical.
              </p>
            </div>
          ) : diffRows.length > 0 ? (
            <table className="min-w-full text-sm">
              <thead className="sticky top-0 z-10 bg-gray-50">
                <tr className="border-gray-200 border-b">
                  <th className="whitespace-nowrap px-3 py-2.5 text-left font-medium text-gray-500 text-xs uppercase tracking-wide">
                    Room
                  </th>
                  <th className="whitespace-nowrap px-3 py-2.5 text-left font-medium text-gray-500 text-xs uppercase tracking-wide">
                    Tenant
                  </th>
                  <th className="whitespace-nowrap px-3 py-2.5 text-left font-medium text-gray-500 text-xs uppercase tracking-wide">
                    Change
                  </th>
                  {diffFields.map((field) => (
                    <th
                      key={field}
                      className="whitespace-nowrap px-3 py-2.5 text-left font-medium text-gray-500 text-xs uppercase tracking-wide"
                    >
                      {FIELD_LABELS[field]}
                    </th>
                  ))}
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-100 bg-white">
                {addedRooms.length > 0 && (
                  <>
                    <tr className="bg-blue-50">
                      <td
                        colSpan={diffFields.length + 3}
                        className="px-3 py-1.5 font-semibold text-blue-800 text-xs uppercase tracking-wide"
                      >
                        Added in B ({addedRooms.length})
                      </td>
                    </tr>
                    {addedRooms.map((row) => (
                      <tr key={row.roomNumber} className="hover:bg-blue-50/40">
                        <td className="whitespace-nowrap px-3 py-2.5 font-semibold text-blue-700">
                          {row.roomNumber}
                        </td>
                        <td className="whitespace-nowrap px-3 py-2.5 text-gray-600">
                          {row.tenantNameB ?? '-'}
                        </td>
                        <td className="px-3 py-2.5">
                          <span className="rounded-full bg-blue-100 px-2 py-0.5 font-semibold text-blue-700 text-xs">
                            Added
                          </span>
                        </td>
                        {diffFields.map((field) => (
                          <td key={field} className="px-3 py-2.5 text-gray-400">
                            -
                          </td>
                        ))}
                      </tr>
                    ))}
                  </>
                )}

                {removedRooms.length > 0 && (
                  <>
                    <tr className="bg-red-50">
                      <td
                        colSpan={diffFields.length + 3}
                        className="px-3 py-1.5 font-semibold text-red-800 text-xs uppercase tracking-wide"
                      >
                        Removed in B ({removedRooms.length})
                      </td>
                    </tr>
                    {removedRooms.map((row) => (
                      <tr key={row.roomNumber} className="hover:bg-red-50/40">
                        <td className="whitespace-nowrap px-3 py-2.5 font-semibold text-red-700">
                          {row.roomNumber}
                        </td>
                        <td className="whitespace-nowrap px-3 py-2.5 text-gray-600">
                          {row.tenantNameA ?? '-'}
                        </td>
                        <td className="px-3 py-2.5">
                          <span className="rounded-full bg-red-100 px-2 py-0.5 font-semibold text-red-700 text-xs">
                            Removed
                          </span>
                        </td>
                        {diffFields.map((field) => (
                          <td key={field} className="px-3 py-2.5 text-gray-400">
                            -
                          </td>
                        ))}
                      </tr>
                    ))}
                  </>
                )}

                {changedRooms.length > 0 && (
                  <>
                    <tr className="bg-amber-50">
                      <td
                        colSpan={diffFields.length + 3}
                        className="px-3 py-1.5 font-semibold text-amber-800 text-xs uppercase tracking-wide"
                      >
                        Changed ({changedRooms.length})
                      </td>
                    </tr>
                    {changedRooms.map((row) => (
                      <tr key={row.roomNumber} className="hover:bg-amber-50/30">
                        <td className="whitespace-nowrap px-3 py-2.5 font-semibold text-gray-800">
                          {row.roomNumber}
                        </td>
                        <td className="whitespace-nowrap px-3 py-2.5 text-gray-600">
                          {row.tenantNameB ?? row.tenantNameA ?? '-'}
                        </td>
                        <td className="px-3 py-2.5">
                          <span className="rounded-full bg-amber-100 px-2 py-0.5 font-semibold text-amber-700 text-xs">
                            Changed
                          </span>
                        </td>
                        {diffFields.map((field) => {
                          const diff = row.diffs[field]
                          if (!diff) {
                            return (
                              <td
                                key={field}
                                className="px-3 py-2.5 text-gray-300"
                              >
                                -
                              </td>
                            )
                          }
                          return (
                            <td key={field} className="px-3 py-2.5">
                              <div className="flex flex-col gap-0.5">
                                <span className="text-red-400 text-xs line-through">
                                  {diff.a || '-'}
                                </span>
                                <span className="font-semibold text-green-700 text-xs">
                                  {diff.b || '-'}
                                </span>
                              </div>
                            </td>
                          )
                        })}
                      </tr>
                    ))}
                  </>
                )}

                {unchangedCount > 0 && (
                  <tr className="bg-gray-50">
                    <td
                      colSpan={diffFields.length + 3}
                      className="px-3 py-2 text-center text-gray-400 text-xs"
                    >
                      {unchangedCount} room{unchangedCount !== 1 ? 's' : ''}{' '}
                      unchanged
                    </td>
                  </tr>
                )}
              </tbody>
            </table>
          ) : null}
        </div>
      </Card>
    </div>
  )
}
